import { Evnt } from '../__types';

function sortEventsByDate(events: Evnt[]): Evnt[] {
  // sorts array of events by date
  return events.sort((e1, e2) => e1.date.valueOf() - e2.date.valueOf());
}

function getEventsByParticipant(events: Evnt[], participant: string): Evnt[] {
  // returns array of events concerning provided participant
  return events.filter(evnt => evnt.participants.includes(participant));
}

function getEventPoint(evnt: Evnt, pixelsPerDay: number): number {
  const milisecondsInDay = 86400000;
  return (evnt.date.valueOf() / milisecondsInDay) * pixelsPerDay;
}

function getEventsPoints(
  events: Evnt[],
  pixelsPerDay: number,
): { [point: number]: Evnt } {
  // returns an object of key points to map on timeline (in pixels) with corresponding events as values
  const sortedEvents = sortEventsByDate(events);
  const first = getEventPoint(sortedEvents[0], pixelsPerDay);
  return sortedEvents.reduce(
    (points: { [point: number]: Evnt }, evnt: Evnt) => {
      const point = getEventPoint(evnt, pixelsPerDay) - first;
      points[point] = evnt;
      return points;
    },
    {},
  );
}

function getClosestEventToDate(events: Evnt[], date: Date): Evnt | null {
  // returns point and event closest to value
  let difference = Infinity;
  let closestEvent = null;
  for (const evnt of sortEventsByDate(events)) {
    const evntDist = Math.abs(evnt.date.valueOf() - date.valueOf());
    if (evntDist <= difference) {
      closestEvent = evnt;
      difference = evntDist;
    }
  }
  return closestEvent;
}

function getEventsByDate(events: Evnt[], date: Date): Evnt[] {
  return sortEventsByDate(events).filter(
    evnt => evnt.date.valueOf() === date.valueOf(),
  );
}

function getEventById(events: Evnt[], id: string): Evnt | null {
  return events.find(evnt => evnt.id === id) || null;
}

function getEventIndex(events: Evnt[], searchEvnt: Evnt): number {
  return events.findIndex(evnt => evnt.id === searchEvnt.id);
}

function getAllEventsUpToDate(events: Evnt[], date: Date): Evnt[] {
  return events.filter(evnt => evnt.date.valueOf() <= date.valueOf());
}

function testEventsForDuplicateId(events: Evnt[]) {
  // not to be used in production
  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'Function testEventsForDuplicateId is not meant to be used in production.',
    );
  }
  return events.some(outerEvnt =>
    events.map(innerEvnt => outerEvnt.id === innerEvnt.id),
  );
}

export {
  sortEventsByDate,
  getEventsByParticipant,
  getEventPoint,
  getEventsPoints,
  getClosestEventToDate,
  getEventsByDate,
  getEventById,
  getEventIndex,
  getAllEventsUpToDate,
  testEventsForDuplicateId,
};
